import React, { useEffect, useState, useRef } from 'react';
import { api } from '../lib/api';
import { Eye, Users } from 'lucide-react';

// ★ 2026-08-11 (Fase 37) — Contatore visite reali "ultimi 7 giorni"
// Utilizzato in:
//   • Card resort della landing pubblica (PublicLanding)
//   • Sotto la tabella appartamenti in ResortDetailPage (La Mar)
//
// Props:
//   • slug           — slug del resort (obbligatorio per fetch)
//   • variant        — 'compact' (badge inline) o 'full' (banner sotto tabella)
//   • preloadedStats — statistiche già caricate (evita fetch duplicato)
//
// Il contatore mostra `unique_visitors` (persone distinte) invece di
// `total_views` per essere più credibile e onesto.

function getOrCreateSessionId() {
  try {
    let sid = localStorage.getItem('gp_visit_session');
    if (!sid) {
      sid = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem('gp_visit_session', sid);
    }
    return sid;
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

// Hook per tracciare la visita (chiamato una sola volta per resort per sessione)
export function useTrackVisit(slug) {
  const trackedRef = useRef(new Set());
  useEffect(() => {
    if (!slug) return;
    if (trackedRef.current.has(slug)) return;
    trackedRef.current.add(slug);
    const sessionId = getOrCreateSessionId();
    api.post('/public/track-visit', {
      slug,
      session_id: sessionId,
      referrer: (document.referrer || '').slice(0, 200),
    }).catch(() => { /* noop */ });
  }, [slug]);
}

export default function VisitsCounter({ slug, variant = 'full', preloadedStats }) {
  const [stats, setStats] = useState(preloadedStats || null);
  const [loading, setLoading] = useState(!preloadedStats);

  useEffect(() => {
    if (preloadedStats) {
      setStats(preloadedStats);
      setLoading(false);
      return;
    }
    if (!slug) return;
    setLoading(true);
    api.get(`/public/visits-stats/${slug}?days=7`)
      .then((r) => setStats(r.data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, [slug, preloadedStats]);

  // Nascondi se non ci sono visite (evita "0 visite" imbarazzante)
  if (loading) return null;
  if (!stats) return null;
  const uniqueVisitors = stats.unique_visitors || 0;
  const totalViews = stats.total_views || 0;
  if (uniqueVisitors === 0 && totalViews === 0) return null;

  // ── Variant compact — badge inline per card landing ────────────────
  if (variant === 'compact') {
    return (
      <div
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-100/80 border border-emerald-200 text-[11px] text-emerald-800"
        data-testid={`visits-counter-compact-${slug}`}
      >
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
        </span>
        <span className="font-semibold">{uniqueVisitors}</span>
        <span className="opacity-80">persone hanno visto questo progetto negli ultimi 7 gg</span>
      </div>
    );
  }

  // ── Variant full — banner sotto tabella ────────────────────────────
  return (
    <div
      className="rounded-xl border border-emerald-200/60 bg-gradient-to-br from-emerald-50 to-white p-4 flex items-center gap-4 flex-wrap"
      data-testid={`visits-counter-${slug}`}
    >
      <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
        <span className="relative flex h-4 w-4">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-4 w-4 bg-emerald-500 items-center justify-center">
            <Users className="w-2.5 h-2.5 text-white" />
          </span>
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-serif text-2xl font-semibold text-emerald-700">{uniqueVisitors}</span>
          <span className="text-sm text-muted-foreground">
            {uniqueVisitors === 1 ? 'persona ha visto' : 'persone hanno visto'} questo progetto negli ultimi 7 giorni
          </span>
        </div>
        {totalViews > uniqueVisitors && (
          <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1">
            <Eye className="w-3 h-3" />
            {totalViews} visualizzazioni totali
          </div>
        )}
      </div>
      <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">
        Live · dati reali
      </div>
    </div>
  );
}
